import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Target, TrendingUp, GitCompare, Users, Bot, ArrowRight,
  ShieldCheck, GraduationCap, Compass, Star, Award, Zap, Lock,
} from "lucide-react";

const STATS = [
  { k: "50K+", v: "Students Guided" },
  { k: "1,200+", v: "Verified Counsellors" },
  { k: "10 yrs", v: "Exam Data" },
  { k: "91%", v: "Prediction Accuracy" },
];

const FEATURES = [
  { icon: Bot, title: "AI Career Coach", desc: "24/7 conversational guidance trained on Indian admissions, exams & career paths.", color: "cyan" },
  { icon: Target, title: "College Predictor", desc: "Feed your rank & category — get Dream, Target and Safe colleges instantly.", color: "indigo" },
  { icon: TrendingUp, title: "Rank Predictor", desc: "JEE, NEET & CUET score-to-rank engine built on a decade of trends.", color: "emerald" },
  { icon: GitCompare, title: "College Comparison", desc: "Compare colleges across 10 parameters — fees, placements, ROI & more.", color: "amber" },
  { icon: Users, title: "Expert Counsellors", desc: "Book 1-on-1 video sessions with verified mentors from IITs & AIIMS.", color: "cyan" },
  { icon: Award, title: "Gamified Journey", desc: "Earn XP, badges & streaks as you build a crystal-clear career roadmap.", color: "rose" },
];

const colorMap = {
  cyan: "from-cyan-400/20 to-cyan-500/5 text-cyan-300 border-cyan-400/20",
  indigo: "from-indigo-400/20 to-indigo-500/5 text-indigo-300 border-indigo-400/20",
  emerald: "from-emerald-400/20 to-emerald-500/5 text-emerald-300 border-emerald-400/20",
  amber: "from-amber-400/20 to-amber-500/5 text-amber-300 border-amber-400/20",
  rose: "from-rose-400/20 to-rose-500/5 text-rose-300 border-rose-400/20",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = user ? "/dashboard" : "/auth";

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white overflow-x-hidden">
      <Seo
        path="/"
        description="EntranceZone AI — AI-powered career counselling, adaptive career assessment, college & rank predictors, scholarships, and verified expert mentorship for Indian students & parents."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "AI Career Counselling & College Admission Guidance",
          "provider": { "@type": "Organization", "name": "EntranceZone AI" },
          "areaServed": "IN",
          "description": "Adaptive AI career assessment, JEE/NEET/CUET rank predictors, college predictor & comparison, scholarships, and 1-on-1 sessions with verified counsellors.",
          "offers": [
            { "@type": "Offer", "name": "Free Explorer", "price": "0", "priceCurrency": "INR" },
            { "@type": "Offer", "name": "Pro Student", "price": "499", "priceCurrency": "INR" },
            { "@type": "Offer", "name": "Premium Career Pass", "price": "999", "priceCurrency": "INR" }
          ]
        }}
      />
      {/* Nav */}
      <header className="sticky top-0 z-50 glass">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 flex items-center justify-between py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="nav-about-btn" onClick={() => navigate("/about")}
              className="hidden sm:inline text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2">
              About
            </button>
            <button data-testid="nav-signin-btn" onClick={() => navigate("/auth")}
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2">
              Sign in
            </button>
            <button data-testid="nav-getstarted-btn" onClick={() => navigate(go)}
              className="rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative radial-fade">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
              <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">AI-Powered Career Intelligence</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Architect your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400 text-glow"> future career</span>
              with AI + human mentors.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Take a 10-question AI assessment, predict your rank & colleges, compare options,
              take mock test/test preparation, and book verified counsellors — all in one intelligent platform.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="hero-start-assessment-btn" onClick={() => navigate(go)}
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500 px-7 py-3.5 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
                Start Free 10-Q Assessment
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <ShieldCheck className="h-4 w-4 text-emerald-400" /> No credit card required
              </div>
            </motion.div>

            {/* Stats ribbon */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.v}>
                  <p className="font-display text-2xl sm:text-3xl font-extrabold text-white">{s.k}</p>
                  <p className="text-xs text-slate-400 mt-1">{s.v}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Hero visual */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-cyan">
              <img src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&w=940&h=650&q=85"
                alt="Indian student planning her career" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="absolute -bottom-5 -left-5 glass rounded-2xl p-4 w-56 border border-cyan-400/20">
              <div className="flex items-center gap-2 mb-2">
                <Compass className="h-4 w-4 text-cyan-400" />
                <span className="text-xs font-semibold text-white">Career Clarity</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: "84%" }} transition={{ delay: 0.9, duration: 1 }}
                  className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" />
              </div>
              <p className="mt-2 font-mono-data text-xs text-cyan-300">84% matched</p>
            </motion.div>
            <div className="absolute -top-4 -right-4 glass rounded-2xl p-3 border border-emerald-400/20">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                <span className="text-sm font-bold">4.9</span>
                <span className="text-xs text-slate-400">/ 1,200 mentors</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Everything you need</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
            One platform for your entire career decision
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-6 transition-all hover:border-cyan-400/30 hover:-translate-y-1">
                <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br border ${colorMap[f.color]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-10 sm:p-16 text-center">
          <div className="absolute inset-0 grid-texture opacity-30" />
          <div className="relative">
            <GraduationCap className="mx-auto h-12 w-12 text-cyan-400 mb-5" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
              Your career deserves more than a guess.
            </h2>
            <p className="mt-4 text-slate-300 max-w-lg mx-auto">
              Join thousands of students making confident, data-backed decisions with EntranceZone AI.
            </p>
            <button data-testid="cta-signup-btn" onClick={() => navigate(go)}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
              <Zap className="h-5 w-5" /> Begin your journey — free
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/8">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <Logo />
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms & Conditions</Link>
              <Link to="/refund" className="hover:text-white transition-colors" data-testid="footer-refund">Refund Policy</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</Link>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact">Contact</Link>
              <Link to="/ez-admin-x9f3q7" className="inline-flex items-center gap-1 text-slate-500 hover:text-amber-300 transition-colors" data-testid="footer-admin">
                <Lock className="h-3 w-3" /> Admin
              </Link>
            </nav>
          </div>
          <p className="mt-8 text-center text-xs text-slate-500">
            © 2026 EntranceZone AI · Architecting Human Potential. Predictions are statistical estimates, not guarantees of admission or outcomes.
          </p>
          <p data-testid="footer-dpdp-line" className="mt-2 text-center text-xs text-slate-400">
            <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-cyan-400" />
            Built for India's DPDP Act, 2023 · Your Personal Data & Privacy Matter · <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
